import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import usePanZoom from '../hooks/usePanZoom';
import { MAX_SCALE, MIN_SCALE, STEP, clampScale } from '../utils/panZoom';

/**
 * A photograph you can magnify: buttons, mouse wheel, double-click or double
 * tap, +/-/0 keys (Shift+arrows move it), drag to move, and two fingers to
 * pinch and move at once on a touch screen (hooks/usePanZoom.js).
 *
 * Used by every collection view through PhotoGallery's viewer, by the
 * verification tool, by micrographs (MicroscopyViewer, with its scale bar as
 * `children`: an overlay laid over the picture in its own pixels, magnified
 * with it), and inline wherever a picture is shown large.
 *
 * Zooming keeps the point under the cursor (or between the fingers) where it
 * is, and moving is clamped to the picture itself, so it cannot be dragged
 * out of view. A new `src` resets the view, so stepping to the next
 * photograph never opens it at the previous one's magnification. Inline
 * (`inline`), one finger on an unmagnified picture still scrolls the page.
 */
export { MAX_SCALE, MIN_SCALE, STEP, clampScale };

/** Keep a scaled picture covering a frame of its own size: it may move at most
 *  (scale-1)/2 of the frame each way. (utils/panZoom.js clampView is the
 *  general rule, for a picture smaller than its frame.) */
export function clampOffset(offset, scale, width, height) {
  const maxX = ((scale - 1) * width) / 2;
  const maxY = ((scale - 1) * height) / 2;
  return {
    x: Math.min(maxX, Math.max(-maxX, offset.x)),
    y: Math.min(maxY, Math.max(-maxY, offset.y)),
  };
}

export default function ZoomableImage({
  src, alt, imageStyle, frameStyle, testId = 'zoomable-image', keyboard = true, inline = false, children,
  imageTestId, onImageLoad, drawing = false,
}) {
  const { t } = useTranslation('common');
  const img = useRef(null);
  const pz = usePanZoom({
    resetKey: src,
    keyboard: keyboard ? 'document' : false,
    inline,
    // An overlay is being drawn on: one pointer is the pen, two still pinch.
    ...(drawing ? { singlePointer: 'ignore', doubleTap: false } : null),
    // The picture as laid out, before the transform: what may be moved.
    measure: () => (img.current ? { width: img.current.offsetWidth, height: img.current.offsetHeight } : null),
  });
  const { view, zoomed, gesturing } = pz;
  const pct = Math.round(view.scale * 100);
  return (
    <div style={{ position: 'relative', ...frameStyle }}>
      <div {...pz.frameProps} data-testid={testId} data-scale={String(Math.round(view.scale * 1000) / 1000)}
           title={t('tip.zoomFrame')}
           // theme-exempt: the backdrop behind a photograph, not behind the page.
           // A page token would turn this near-white in lab_light and wash the
           // image out; an image is viewed against dark whatever the theme is.
           style={{ ...pz.frameProps.style, overflow: 'hidden', background: '#111', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    cursor: zoomed ? (gesturing ? 'grabbing' : 'grab') : 'zoom-in' }}>
        <div className="pz-content" style={{ position: 'relative', lineHeight: 0, ...pz.contentStyle,
          transition: gesturing ? 'none' : 'transform 0.12s ease-out' }}>
          <img ref={img} src={src} alt={alt} draggable={false} data-testid={imageTestId} onLoad={onImageLoad}
               style={{ display: 'block', maxWidth: '94vw', maxHeight: '80vh', objectFit: 'contain', userSelect: 'none',
                        ...imageStyle }} />
          {children}
        </div>
      </div>
      <div style={toolbar} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={pz.zoomOut} disabled={!zoomed}
                aria-label={t('photoViewer.zoomOut')} title={t('tip.zoomOut')} style={toolButton}>−</button>
        <button type="button" onClick={pz.reset} disabled={!zoomed}
                aria-label={t('photoViewer.zoomReset')} title={t('tip.zoomReset')}
                style={{ ...toolButton, width: 'auto', padding: '0 8px', fontSize: 12 }}>
          {pct}%
        </button>
        <button type="button" onClick={pz.zoomIn} disabled={view.scale >= MAX_SCALE}
                aria-label={t('photoViewer.zoomIn')} title={t('tip.zoomIn')} style={toolButton}>+</button>
      </div>
      {/* The magnification, said once it settles. */}
      <span aria-live="polite" style={srOnly}>{zoomed ? t('photoViewer.zoomLevel', { pct }) : ''}</span>
    </div>
  );
}

const toolbar = {
  position: 'absolute', bottom: 8, insetInlineStart: 8, display: 'flex', gap: 4,
  background: 'rgba(0,0,0,0.55)', borderRadius: 16, padding: 3,
};
const toolButton = {
  background: 'transparent', color: 'var(--c-on-accent)', border: 'none', borderRadius: 13, width: 26, height: 26,
  fontSize: 18, lineHeight: '24px', cursor: 'pointer', padding: 0,
};
const srOnly = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap', border: 0,
};
